import { Row, Col, Card } from 'react-bootstrap';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useAppContext } from '@/Main';
import Flex from '@/components/common/Flex';
import IconItem from '@/components/common/icon/IconItem';
import SubtleBadge from '@/components/common/SubtleBadge';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import { getHelpHtml } from '../../utils/help/help-content';

interface PromptsSummaryCardsProps {
  total: number;
  pending: number;
  executed: number;
  failed: number;
  needsReview: number;
}

export function PromptsSummaryCards({
  total,
  pending,
  executed,
  failed,
  needsReview,
}: PromptsSummaryCardsProps) {
  const {
    config: { themeVariant },
  } = useAppContext() as { config: { themeVariant?: string } };
  const lightPrimary = themeVariant === 'primary';
  const coverage = total > 0 ? Math.round((executed / total) * 100) : 0;

  const cards: Array<{
    label: string;
    helpKey: string;
    value: number;
    sub: string;
    accent: string;
    icon: IconProp;
    color: string;
  }> = [
    {
      label: 'مجموع پرامپت‌ها',
      helpKey: 'total_prompts',
      value: total,
      sub: 'در همه تاپیک‌ها',
      accent: 'text-1000',
      icon: 'list-alt',
      color: 'primary',
    },
    {
      label: 'آماده اجرا',
      helpKey: 'ready_to_run',
      value: pending,
      sub: `${failed} ناموفق`,
      accent: 'text-warning',
      icon: 'clock',
      color: 'warning',
    },
    {
      label: 'اجرا شده',
      helpKey: 'executed',
      value: executed,
      sub: `${coverage}% کاوریج`,
      accent: 'text-success',
      icon: 'check-circle',
      color: 'success',
    },
    {
      label: 'نیاز به بررسی',
      helpKey: 'needs_review',
      value: needsReview,
      sub: 'برندی یافت نشد',
      accent: 'text-primary',
      icon: 'exclamation-circle',
      color: 'info',
    },
  ];

  return (
    <Row xs={2} xl={4} className="g-3">
      {cards.map(card => (
        <Col key={card.label}>
          <Card className="h-100 shadow-sm">
            <Card.Body className="p-3">
              <Flex justifyContent="between" alignItems="center" className="gap-2">
                <div className="min-w-0 d-flex flex-column gap-3">
                  <p className="fs--1 fw-medium text-500 mb-0">
                    <span className="d-inline-flex align-items-center gap-1">
                      {card.label}
                      <HelpTooltip html={getHelpHtml(card.helpKey)} />
                    </span>
                  </p>
                  <Flex alignItems="center" className="gap-2 flex-wrap">
                    <p className={`fs-3 fw-bold mb-0 ${card.accent}`}>{card.value}</p>
                    <SubtleBadge bg="secondary" pill className="fs--2 fw-medium" style={{ lineHeight: 1 }}>
                      {card.sub}
                    </SubtleBadge>
                  </Flex>
                </div>
                <IconItem
                  tag="div"
                  icon={card.icon}
                  bg={card.color === 'primary' ? undefined : card.color}
                  color={card.color === 'primary' ? undefined : 'white'}
                  iconClass="fs-0"
                  className="shadow-none flex-shrink-0 align-self-center"
                  style={
                    card.color === 'primary'
                      ? {
                          backgroundColor: 'var(--falcon-primary)',
                          color: lightPrimary ? 'var(--falcon-primary-text-emphasis)' : '#fff',
                        }
                      : undefined
                  }
                />
              </Flex>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}
